import { useCallback, useState } from 'react';
import { toast } from 'sonner';

const createEmptyNewMember = () => ({
  name: '',
  email: '',
  phone: '',
  tier: 'member',
  status: 'active',
  billing_cycle: 'monthly',
  cohorts: '',
  relationship_stage: 'prospect',
  campaign_type: '',
  source: '',
  warmth: '',
  children_age_range: '',
  biggest_pressure_area: '',
  notes: '',
});

export function useAddMemberForm({ adminFetch, fetchMembers }) {
  const [showAddModal, setShowAddModal] = useState(false);
  const [newMember, setNewMember] = useState(createEmptyNewMember);
  const [newMemberPhotoFile, setNewMemberPhotoFile] = useState(null);
  const [adding, setAdding] = useState(false);

  const resetForm = useCallback(() => {
    setNewMember(createEmptyNewMember());
    setNewMemberPhotoFile(null);
  }, []);

  const closeAddModal = useCallback(() => {
    resetForm();
    setShowAddModal(false);
  }, [resetForm]);

  const openAddModal = useCallback(() => {
    setShowAddModal(true);
  }, []);

  const handleAddMember = useCallback(async () => {
    if (!newMember?.name?.trim() || !newMember?.email?.trim()) {
      toast.error('Name and Email are required.');
      return;
    }

    setAdding(true);
    try {
      const payload = {
        name: newMember.name.trim(),
        email: newMember.email.trim().toLowerCase(),
        phone: (newMember.phone || '').trim(),
        tier: newMember.tier || 'member',
        status: newMember.status || 'active',
        billing_cycle: newMember.billing_cycle || 'monthly',
        cohorts: (newMember.cohorts || '')
          .split(',')
          .map((value) => value.trim())
          .filter(Boolean),
        relationship_stage: newMember.relationship_stage || 'prospect',
        campaign_type: newMember.campaign_type || null,
        source: newMember.source || null,
        warmth: newMember.warmth || null,
        children_age_range: newMember.children_age_range || null,
        biggest_pressure_area: newMember.biggest_pressure_area || null,
        notes: newMember.notes || null,
      };

      const response = await adminFetch('/admin/members', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        toast.error(error?.detail || `Failed to add member (${response.status})`);
        return;
      }

      const data = await response.json().catch(() => ({}));
      const userId = data?.user?.user_id;

      if (newMemberPhotoFile && userId) {
        const formData = new FormData();
        formData.append('file', newMemberPhotoFile);
        const photoResponse = await adminFetch(`/admin/members/${userId}/photo`, {
          method: 'POST',
          body: formData,
        });

        if (!photoResponse.ok) {
          toast.error('Dad added but headshot upload failed. You can add a photo in Edit.');
        }
      }

      toast.success('Dad added!');
      closeAddModal();
      await fetchMembers();
    } catch (error) {
      console.error('handleAddMember error:', error);
      toast.error(error?.message || 'Failed to add member');
    } finally {
      setAdding(false);
    }
  }, [adminFetch, closeAddModal, fetchMembers, newMember, newMemberPhotoFile]);

  return {
    showAddModal,
    openAddModal,
    closeAddModal,
    newMember,
    setNewMember,
    newMemberPhotoFile,
    setNewMemberPhotoFile,
    adding,
    handleAddMember,
  };
}
